import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocRun,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { allocateWords } from "@/lib/tool-kit/essay-templates/research-proposal";
import {
  countWords,
  listOrPrompt,
  paragraphsOrPrompt,
  parseWholeCount,
  splitItems,
  splitLines,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * An op-ed outline: a lede pegged to the news, a one-sentence argument, three points
 * each carrying its own evidence and a "so what", a "to be sure" paragraph that meets the
 * strongest objection, and a conclusion that closes on a call to action.
 *
 * The shape is the one The OpEd Project teaches: lede, argument, three points with two
 * pieces of evidence each, a "to be sure" paragraph, and a conclusion that often circles
 * back to the lede. The 650–800 word range is this tool's working budget: outlets set
 * their own limits, and the page says to check the one you are pitching.
 */

export const OP_ED_RANGE = { low: 650, high: 800 } as const;
export const DEFAULT_OP_ED_WORDS = 750;
export const MIN_OP_ED_WORDS = 300;
export const MAX_OP_ED_WORDS = 1500;
export const POINT_COUNT = 3;

export interface OpEdPoint {
  /** The point as a sentence. */
  claim: string;
  /** Evidence, one piece per line. */
  evidence: string;
  /** What it means: why the reader should care. */
  soWhat: string;
}

export interface OpEdInput {
  headline: string;
  words: string;
  newsHook: string;
  lede: string;
  argument: string;
  points: OpEdPoint[];
  objection: string;
  response: string;
  action: string;
}

export function emptyPoints(): OpEdPoint[] {
  return Array.from({ length: POINT_COUNT }, () => ({
    claim: "",
    evidence: "",
    soWhat: "",
  }));
}

export const OP_ED_PARTS = [
  "lede",
  "argument",
  "point1",
  "point2",
  "point3",
  "objection",
  "conclusion",
] as const;
export type OpEdPart = (typeof OP_ED_PARTS)[number];

/** A typical split of the piece, in percent. A convention for pacing, not a rule. */
export const OP_ED_SHARES: Record<OpEdPart, number> = {
  lede: 12,
  argument: 10,
  point1: 18,
  point2: 18,
  point3: 18,
  objection: 12,
  conclusion: 12,
};

export function opEdTotal(raw: string): number {
  const total = parseWholeCount(raw);
  if (total === null || total < MIN_OP_ED_WORDS || total > MAX_OP_ED_WORDS) {
    return DEFAULT_OP_ED_WORDS;
  }
  return total;
}

export function opEdTargets(total: number): Record<OpEdPart, number> {
  const allocated = allocateWords(
    total,
    OP_ED_PARTS.map((part) => OP_ED_SHARES[part]),
    5
  );
  return Object.fromEntries(
    OP_ED_PARTS.map((part, index) => [part, allocated[index]])
  ) as Record<OpEdPart, number>;
}

/** The writer's words in each part; the news hook is a note to self and does not count. */
export function opEdWords(input: OpEdInput): Record<OpEdPart, number> {
  const point = (index: number) =>
    countWords(input.points[index].claim) +
    countWords(input.points[index].evidence) +
    countWords(input.points[index].soWhat);
  return {
    lede: countWords(input.lede),
    argument: countWords(input.argument),
    point1: point(0),
    point2: point(1),
    point3: point(2),
    objection: countWords(input.objection) + countWords(input.response),
    conclusion: countWords(input.action),
  };
}

export interface OpEdMeter {
  total: number;
  target: number;
  parts: Record<OpEdPart, number>;
  targets: Record<OpEdPart, number>;
  inRange: boolean;
}

export function measureOpEd(input: OpEdInput): OpEdMeter {
  const parts = opEdWords(input);
  const target = opEdTotal(input.words);
  const total = OP_ED_PARTS.reduce((sum, part) => sum + parts[part], 0);
  return {
    total,
    target,
    parts,
    targets: opEdTargets(target),
    inRange: total >= OP_ED_RANGE.low && total <= OP_ED_RANGE.high,
  };
}

function has(text: string): boolean {
  return text.trim() !== "";
}

/**
 * A sentence break: terminal punctuation after at least three lower-case letters or
 * digits, then a space and a capital — so "e.g. Austin", "Dr. Lee" and "U.S. cities" do
 * not count as two sentences.
 */
const SENTENCE_BREAK = /(?<=[a-z0-9]{3,}[.!?…]["”’)\]]*)\s+\p{Lu}/u;

export type OpEdCheckId =
  | "hook"
  | "lede"
  | "argument"
  | "oneSentence"
  | "points"
  | "evidence"
  | "objection"
  | "action"
  | "range";

export interface OpEdCheck {
  id: OpEdCheckId;
  state: "done" | "todo" | "note";
  /** Point numbers (from 1) the message refers to. */
  items?: number[];
}

export function opEdChecks(input: OpEdInput): OpEdCheck[] {
  const meter = measureOpEd(input);
  const noClaim = input.points
    .map((p, i) => (has(p.claim) ? 0 : i + 1))
    .filter((n) => n > 0);
  const noEvidence = input.points
    .map((p, i) => (has(p.claim) && !has(p.evidence) ? i + 1 : 0))
    .filter((n) => n > 0);
  let range: OpEdCheck["state"] = "done";
  if (meter.total === 0) {
    range = "todo";
  } else if (!meter.inRange) {
    range = "note";
  }
  return [
    { id: "hook", state: has(input.newsHook) ? "done" : "note" },
    { id: "lede", state: has(input.lede) ? "done" : "todo" },
    { id: "argument", state: has(input.argument) ? "done" : "todo" },
    {
      id: "oneSentence",
      state:
        has(input.argument) && SENTENCE_BREAK.test(input.argument.trim())
          ? "note"
          : "done",
    },
    noClaim.length === 0
      ? { id: "points", state: "done" }
      : { id: "points", state: "todo", items: noClaim },
    noEvidence.length === 0
      ? { id: "evidence", state: "done" }
      : { id: "evidence", state: "note", items: noEvidence },
    {
      id: "objection",
      state: has(input.objection) && has(input.response) ? "done" : "todo",
    },
    { id: "action", state: has(input.action) ? "done" : "todo" },
    { id: "range", state: range },
  ];
}

/* ------------------------------------------------------------------ document */

export interface OpEdLabels {
  defaultTitle: string;
  subtitle: string;
  headings: Record<OpEdPart | "budget", string>;
  target: string;
  tips: Record<OpEdPart, string>;
  prompts: {
    hook: string;
    lede: string;
    argument: string;
    claim: string;
    evidence: string;
    soWhat: string;
    objection: string;
    response: string;
    action: string;
  };
  hookLabel: string;
  claimLead: string;
  soWhatLead: string;
  objectionLead: string;
  responseLead: string;
  budgetHead: { part: string; target: string; yours: string };
  budgetTotal: string;
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

function guidance(
  part: OpEdPart,
  labels: OpEdLabels,
  meter: OpEdMeter,
  fmt: Format
): DocBlock {
  return {
    kind: "guidance",
    text: `${t(labels.target, { words: fmt(meter.targets[part]) })} ${labels.tips[part]}`,
  };
}

function leadRun(text: string, lead: string): DocRun[] {
  return [{ text: `${lead} `, bold: true }, { text }];
}

function pointBlocks(point: OpEdPoint, labels: OpEdLabels): DocBlock[] {
  const claim = splitLines(point.claim).join(" ");
  const soWhat = splitLines(point.soWhat).join(" ");
  return [
    claim
      ? { kind: "paragraph", text: leadRun(claim, labels.claimLead) }
      : { kind: "prompt", text: labels.prompts.claim },
    ...(splitItems(point.evidence).length > 0
      ? listOrPrompt(point.evidence, "bullet", labels.prompts.evidence)
      : [{ kind: "prompt", text: labels.prompts.evidence } as DocBlock]),
    soWhat
      ? { kind: "paragraph", text: leadRun(soWhat, labels.soWhatLead) }
      : { kind: "prompt", text: labels.prompts.soWhat },
  ];
}

export function buildOpEdDocument(
  input: OpEdInput,
  labels: OpEdLabels,
  fmt: Format
): DocumentModel {
  const meter = measureOpEd(input);
  const hook = input.newsHook.trim();
  const sections: DocSection[] = [
    {
      heading: labels.headings.budget,
      blocks: [
        {
          kind: "table",
          head: [
            labels.budgetHead.part,
            labels.budgetHead.target,
            labels.budgetHead.yours,
          ],
          rows: [
            ...OP_ED_PARTS.map((part) => [
              labels.headings[part],
              fmt(meter.targets[part]),
              fmt(meter.parts[part]),
            ]),
            [labels.budgetTotal, fmt(meter.target), fmt(meter.total)],
          ],
        },
      ],
    },
    {
      heading: labels.headings.lede,
      blocks: [
        guidance("lede", labels, meter, fmt),
        hook
          ? {
              kind: "fields",
              fields: [{ label: labels.hookLabel, value: hook }],
            }
          : { kind: "prompt", text: labels.prompts.hook },
        ...paragraphsOrPrompt(input.lede, labels.prompts.lede),
      ],
    },
    {
      heading: labels.headings.argument,
      blocks: [
        guidance("argument", labels, meter, fmt),
        ...paragraphsOrPrompt(input.argument, labels.prompts.argument),
      ],
    },
    ...(["point1", "point2", "point3"] as const).map(
      (part, index): DocSection => ({
        heading: labels.headings[part],
        blocks: [
          guidance(part, labels, meter, fmt),
          ...pointBlocks(input.points[index], labels),
        ],
      })
    ),
    {
      heading: labels.headings.objection,
      blocks: [
        guidance("objection", labels, meter, fmt),
        has(input.objection)
          ? {
              kind: "paragraph",
              text: leadRun(
                splitLines(input.objection).join(" "),
                labels.objectionLead
              ),
            }
          : { kind: "prompt", text: labels.prompts.objection },
        has(input.response)
          ? {
              kind: "paragraph",
              text: leadRun(
                splitLines(input.response).join(" "),
                labels.responseLead
              ),
            }
          : { kind: "prompt", text: labels.prompts.response },
      ],
    },
    {
      heading: labels.headings.conclusion,
      blocks: [
        guidance("conclusion", labels, meter, fmt),
        ...paragraphsOrPrompt(input.action, labels.prompts.action),
      ],
    },
  ];
  return {
    title: input.headline.trim() || labels.defaultTitle,
    subtitle: t(labels.subtitle, { words: fmt(meter.target) }),
    sections,
  };
}
